/**
 * Client-side user profile helpers — mirrors automationtool/app.py entitlement logic.
 */

export const FREE_DAILY_PROCESSING_LIMIT = 3;

export function utcDateString() {
  return new Date().toISOString().slice(0, 10);
}

export function normalizeFreeRunsToday(firestoreData) {
  const today = utcDateString();
  const stored = String(firestoreData?.free_runs_utc_date || '');
  if (stored !== today) {
    return 0;
  }
  return Math.max(0, parseInt(firestoreData?.free_runs_today, 10) || 0);
}

export function freemiumFieldsFromFirestore(reconciled, planTier) {
  const today = utcDateString();
  if (planTier === 'premium') {
    return {
      free_runs_today: null,
      free_runs_limit: null,
      free_runs_remaining: null,
      free_runs_utc_date: today,
    };
  }
  const count = normalizeFreeRunsToday(reconciled);
  const limit = FREE_DAILY_PROCESSING_LIMIT;
  return {
    free_runs_today: count,
    free_runs_limit: limit,
    free_runs_remaining: Math.max(0, limit - count),
    free_runs_utc_date: today,
  };
}

export function parseIsoUtc(value) {
  if (!value || typeof value !== 'string') return null;
  const s = value.trim();
  if (!s) return null;
  try {
    const normalized = s.endsWith('Z') ? `${s.slice(0, -1)}+00:00` : s;
    const d = new Date(normalized);
    if (Number.isNaN(d.getTime())) return null;
    return d;
  } catch {
    return null;
  }
}

export function isCouponTrialActive(userData) {
  if (!userData || typeof userData !== 'object') return false;
  const endIso = userData.coupon_trial_end_at_iso || userData.coupon_trial_end_at;
  if (!endIso || typeof endIso !== 'string') return false;
  const endDt = parseIsoUtc(endIso);
  if (!endDt) return false;
  return Date.now() < endDt.getTime();
}

/** Lazy wall-clock expiry — align with server _get_user_subscription_snapshot guard. */
export function reconcileSubscriptionFields(userData) {
  if (!userData || typeof userData !== 'object') return {};
  const data = { ...userData };
  let status = String(data.subscription_status || '').toLowerCase();
  if (status === 'active' || status === 'cancelled') {
    const endIso = data.subscription_current_end_iso || data.subscription_current_end_at;
    const endDt = parseIsoUtc(typeof endIso === 'string' ? endIso : '');
    if (endDt && Date.now() >= endDt.getTime()) {
      data.subscription_status = 'expired';
    }
  }
  return data;
}

export function getPlanTierForUser(userData) {
  const data = reconcileSubscriptionFields(userData || {});
  const status = String(data.subscription_status || '').toLowerCase();
  if (status === 'active') return 'premium';
  if (status === 'cancelled') {
    const endIso = data.subscription_current_end_iso || data.subscription_current_end_at;
    const endDt = parseIsoUtc(typeof endIso === 'string' ? endIso : '');
    if (endDt && Date.now() < endDt.getTime()) return 'premium';
  }
  if (isCouponTrialActive(data)) return 'premium';
  return 'free';
}

export function computeTimeSavedSeconds(userData) {
  const total = Math.floor(Number(userData?.total_video_time_processed) || 0);
  return total * 15;
}

/** Map a Firestore users/{uid} document into fields merged onto useAuth user. */
export function profileFieldsFromFirestoreDoc(firestoreData) {
  if (!firestoreData || typeof firestoreData !== 'object') {
    return {
      plan_tier: 'free',
      time_saved_seconds: 0,
      total_videos_uploaded: 0,
      free_runs_today: 0,
      free_runs_limit: FREE_DAILY_PROCESSING_LIMIT,
      free_runs_remaining: FREE_DAILY_PROCESSING_LIMIT,
      free_runs_utc_date: utcDateString(),
    };
  }

  const reconciled = reconcileSubscriptionFields(firestoreData);
  let streakCount = parseInt(reconciled.creator_streak_count, 10) || 0;
  if (streakCount < 1 && reconciled.creator_streak_last_date) {
    streakCount = 1;
  }

  const couponTrialEnd =
    reconciled.coupon_trial_end_at_iso || reconciled.coupon_trial_end_at || '';
  const planTier = getPlanTierForUser(reconciled);

  return {
    subscription_status: reconciled.subscription_status || '',
    plan_tier: planTier,
    coupon_code_applied: reconciled.coupon_code_applied,
    coupon_trial_end_at_iso: couponTrialEnd || undefined,
    premium_source: reconciled.premium_source || '',
    creator_streak_count: streakCount,
    creator_streak_last_date: reconciled.creator_streak_last_date,
    push_status: reconciled.push_status,
    reminders_enabled: Boolean(reconciled.reminders_enabled),
    phone_number: reconciled.phone_number || null,
    time_saved_seconds: computeTimeSavedSeconds(reconciled),
    total_videos_uploaded: Math.max(0, parseInt(reconciled.total_videos_uploaded, 10) || 0),
    subscription_charge_at_iso: reconciled.subscription_charge_at_iso || '',
    subscription_current_end_iso: reconciled.subscription_current_end_iso || '',
    ...freemiumFieldsFromFirestore(reconciled, planTier),
  };
}
